import { formatGel } from '@/utils/money';
/** One clause of the line under an invoice total, ready for `t(key, params)`. */
export type BreakdownSegment = {
    key: string;
    params: Record<string, string>;
};

type BreakdownInput = {
    /** Σ `purchasePrice × quantity` across the invoice's lines. */
    linesTotalTetri: number;
    /** Ancillary charges — freight, customs, … */
    expensesTotalTetri: number;
    /**
     * VAT the supplier charged on the lines. Undefined on a draft or a pending
     * invoice: nothing has been posted, so there is no rate-dated figure to
     * show and the older lines-plus-expenses phrasing is the honest one.
     */
    vatTotalTetri?: number;
    /** `linesTotal + vatTotal`, as the server computed it. */
    grossTotalTetri?: number;
    /** The part of `expensesTotal` the supplier billed on their own invoice. */
    expensesOnInvoiceTotalTetri?: number;
};

// BOS-162: `formatGel` without the symbol. The clause reads "Lines 200.00 + VAT
// 36.00 = 236.00", where the ₾ belongs to the headline above rather than to
// every figure in the sentence under it.
const money = (tetri: number): string =>
    formatGel(tetri, { withSymbol: false });

/**
 * The figure at the top of an invoice row.
 *
 * `payableTotalTetri` when there is one — what the supplier is owed, which is
 * what an accountant matches against paper. There is one on a **received**
 * invoice, where `ReceivedInvoice` declares it required.
 *
 * The fallback is not defence against a server that omits the field (BOS-171):
 * `purchasesMapping` twins a required field, so an absent one would arrive as
 * `0` and never reach here as `undefined`. It is for the other two shapes this
 * component renders — a **draft** and a **pending** invoice, whose types carry
 * no `payableTotal` because nothing has posted and there is no payable yet.
 * Those headline the landed cost, which is the only total they have.
 */
export const headlineTotal = ({
    totalAmountTetri,
    payableTotalTetri,
}: {
    totalAmountTetri: number;
    payableTotalTetri?: number;
}): number => payableTotalTetri ?? totalAmountTetri;

/**
 * The clauses under an invoice total.
 *
 * A total that is a sum of several things has to say which things, and after
 * bos-api BOS-125 there are three that read differently: the goods, the tax the
 * supplier charged on them, and the ancillary charges — which may be owed to
 * the supplier or to whoever carried the goods, and only the first kind lands
 * on the supplier's balance. Reading "88.50" with no explanation next to a
 * supplier card showing 108.50 is how the two figures came to look like a bug.
 *
 * Returns an empty list when there is nothing worth explaining — an invoice of
 * lines alone is already fully described by its headline number.
 */
export const buildBreakdownSegments = ({
    linesTotalTetri,
    expensesTotalTetri,
    vatTotalTetri,
    grossTotalTetri,
    expensesOnInvoiceTotalTetri,
}: BreakdownInput): BreakdownSegment[] => {
    const segments: BreakdownSegment[] = [];

    if (vatTotalTetri === undefined || grossTotalTetri === undefined) {
        // Draft / pending: no posting, so no VAT figure to stand behind.
        if (expensesTotalTetri > 0) {
            segments.push({
                key: 'purchases.invoiceTotal.breakdown',
                params: {
                    lines: money(linesTotalTetri),
                    expenses: money(expensesTotalTetri),
                },
            });
        }
        return segments;
    }

    segments.push({
        key: 'purchases.invoiceTotal.linesWithVat',
        params: {
            lines: money(linesTotalTetri),
            vat: money(vatTotalTetri),
            gross: money(grossTotalTetri),
        },
    });

    const onInvoice = expensesOnInvoiceTotalTetri ?? 0;
    const offInvoice = expensesTotalTetri - onInvoice;

    // The last clause is the one that explains the journal entry. The posting
    // credits the supplier for the gross plus their own charges, and the
    // carrier's clearing account for the rest, so the entry's total is
    // `grossTotal + expensesTotal` — 276.00 where the headline reads 236.00.
    // Without this the two figures look like different answers to one question,
    // which is exactly how the audit read them.
    const bookedTotalTetri = grossTotalTetri + expensesTotalTetri;

    if (onInvoice > 0) {
        segments.push({
            key: 'purchases.invoiceTotal.expensesOnInvoice',
            params: { expenses: money(onInvoice) },
        });
    }
    if (offInvoice > 0) {
        segments.push({
            key: 'purchases.invoiceTotal.expensesOffInvoice',
            params: { expenses: money(offInvoice) },
        });
    }

    if (expensesTotalTetri > 0) {
        segments.push({
            key: 'purchases.invoiceTotal.booked',
            params: { booked: money(bookedTotalTetri) },
        });
    }

    return segments;
};
